import { ReactElement, ReactNode, SyntheticEvent, useEffect, useState } from "react";
import { Divider, Grid, Tab, Tabs, Typography } from "@mui/material";

import GlobalPopUp from "../../../utility/globalPopUp";
import ConfigurationPopupFooter from "./ConfigurationPopupFooter";

import popupStyles from "../../../../assets/styles/componentStyles/globalPopupStyles.module.scss";
import globalStyles from "../../../../assets/styles/global.module.scss";
import styles from "../../../../assets/styles/componentStyles/chatbotStyles/chatbotIntegration/chatbotIntegrationPopup.module.scss";
import useGenericHook from "../../../../hooks/useGenericHook";

interface TabItem {
    id: string;
    label: string;
    icon?: ReactNode;
    disabled?: boolean;
    component: ReactNode;
    footer?: ReactNode;
}

interface ConfigurationPopupProps {
    open: boolean;
    onClose: () => void;
    title: string;
    header?: ReactNode;
    tabs: TabItem[];
    footer?: ReactNode;
    width?: string;
    maxWidth?: string;
    defaultTab?: number;
}

const ConfigurationPopup = (props: ConfigurationPopupProps) => {

    const { open, onClose, title, header, tabs, footer, width = "850px", maxWidth = "850px", defaultTab = 0 } = props;

    const { isScreenBelow600, isScreenBelow1000 } = useGenericHook();

    const [activeTab, setActiveTab] = useState(defaultTab);

    const handleTabChange = (_: SyntheticEvent, value: number) => {
        setActiveTab(value);
    };

    useEffect(() => {
        if (open) {
            setActiveTab(defaultTab);
        }
    }, [open, defaultTab]);

    return (
        <GlobalPopUp
            open={open}
            onClose={onClose}
            handleClose={onClose}
            // flagBackdrop
            flagShowCloseIcon
            titlePadding="0"
            paperMinWidth={isScreenBelow600 ? "320px" : isScreenBelow1000 ? "560px" : width}
            paperMaxwidth={isScreenBelow600 ? "610px" : isScreenBelow1000 ? "800px" : maxWidth}
        >
            <Grid container className={`${globalStyles.flexColumn} ${globalStyles.justifyCenter}`}>

                {/* Popup Title */}
                <Grid item className={globalStyles.textCenter}>
                    <Typography className={popupStyles.popupTitleTypography}>
                        {title}
                    </Typography>
                </Grid>

                <Divider className={popupStyles.popupTitleDivider} />

                {/* Popup Body */}
                <Grid item className={globalStyles.fullwidth}>
                    <Grid
                        container
                        className={popupStyles.popupBodyGridContainer}
                        gap={2}
                    >

                        {/* Optional Header */}
                        {header && (
                            <>
                                <Grid item xs={12}>
                                    {header}
                                </Grid>
                            </>
                        )}

                        {/* Tabs */}
                        <Grid item xs={12}>
                            {(tabs.length > 0) && (
                                <>
                                    <Grid item xs={12} className={styles.configurationTabsContainer}>
                                        <Tabs
                                            value={activeTab}
                                            onChange={handleTabChange}
                                            variant="scrollable"
                                            scrollButtons="auto"
                                            allowScrollButtonsMobile
                                            className={styles.configurationTabs}
                                        >
                                            {tabs.map((tab, index) => (
                                                <Tab
                                                    key={tab.id}
                                                    value={index}
                                                    label={tab.label}
                                                    {...(tab.icon
                                                        ? { icon: tab.icon as ReactElement, iconPosition: "start" as const }
                                                        : {})}
                                                    disabled={tab.disabled}
                                                    className={styles.configurationTab}
                                                />
                                            ))}
                                        </Tabs>

                                        <Divider className={styles.configurationTabsDivider} />
                                    </Grid>

                                    {/* Active Tab */}
                                    <Grid
                                        item
                                        xs={12}
                                        className={styles.configurationTabPanel}
                                        onKeyDown={(e) => e.stopPropagation()}
                                    >
                                        {tabs[activeTab]?.component}
                                    </Grid>
                                </>
                            )}
                        </Grid>

                    </Grid>
                </Grid>

                {/* Footer */}
                {(tabs[activeTab]?.footer || footer) && (
                    <ConfigurationPopupFooter>
                        {tabs[activeTab]?.footer || footer}
                    </ConfigurationPopupFooter>
                )}

            </Grid>

        </GlobalPopUp>
    );
};

export default ConfigurationPopup;